import moment from "moment";
import ApiCalls from "../../api";
import { store } from "../../redux/store";
import { ThreadStoreActions } from "../../redux/thread-store";
import MessageHelper from "../message/message-helper";

const PrivateMethods = {};

const ThreadHelper = {
  // Public
  // This should be called when the search text changes
  // It will reset the pagination and fetch the threads list
  loadThreadsList: async () => {
    const { threadStore } = store.getState();
    const { searchText } = threadStore;
    // clear the threads list
    store.dispatch(ThreadStoreActions.setAllThreads([]));
    let threads = [];
    try {
      ThreadHelper.showLoader();
      const response = await ApiCalls.thread.private.getThreads({
        searchText,
        page: 1,
        limit: 50,
      });
      console.log("response :>> ", response);
      if (response?.data?.data) {
        const { docs, totalDocs, limit, page, totalPages, hasNextPage } =
          response.data.data;
        store.dispatch(
          ThreadStoreActions.setPagination({
            limit,
            totalPages,
            page,
            hasNextPage,
            totalDocs,
          })
        );
        threads = docs;
      }
    } catch (error) {
      console.error("Error fetching threads", error);
    }
    ThreadHelper.hideLoader();
    store.dispatch(ThreadStoreActions.setAllThreads(threads));
  },
  loadNextPage: async () => {
    const { threadStore } = store.getState();
    const { searchText, pagination, allThreads } = threadStore;
    const { page, limit, totalPages, hasNextPage } = pagination;

    console.log('loadNextPage called:', { page, totalPages, hasNextPage });

    if (!hasNextPage || page >= totalPages) {
      console.log('No more pages to load');
      return;
    }

    let newThreads = [];
    try {
      console.log('Fetching page:', page + 1);
      const response = await ApiCalls.thread.private.getThreads({
        searchText,
        page: page + 1,
        limit,
      });
      if (response?.data?.data) {
        const { docs, totalDocs, limit, page, totalPages, hasNextPage } =
          response.data.data;
        console.log('API response pagination:', { page, totalPages, hasNextPage, docsCount: docs.length });
        store.dispatch(
          ThreadStoreActions.setPagination({
            limit,
            totalPages,
            page,
            hasNextPage,
            totalDocs,
          })
        );
        newThreads = docs;
        // Append new threads to existing ones instead of replacing
        store.dispatch(ThreadStoreActions.appendThreads(newThreads));
      }
    } catch (error) {
      console.error("Error fetching threads", error);
    }
  },


  createNewThread: async ({ title, contactId }) => {
    try {
      ThreadHelper.showLoader();
      const response = await ApiCalls.thread.private.createThread({
        type: "individual",
        title,
        _contacts: [contactId],
        _users: [],
      });
      if (response?.data?.data) {
        ThreadHelper.loadThreadsList();
      }
    } catch (error) {
      console.error("Error creating thread", error);
    }
    ThreadHelper.hideLoader();
  },
  makeThreadActive: async (thread) => {
    // Only update in the store
    store.dispatch(ThreadStoreActions.setSelectedThread(thread));
    // Load thread messages
    MessageHelper.loadThreadMessages(thread._id);
    ThreadHelper.markThreadAsRead(thread._id);
  },
  makeThreadInactive: async () => {
    // Only update in the store
    store.dispatch(ThreadStoreActions.setSelectedThread(null));
  },
  markThreadAsRead: async (threadId) => {
    try {
      await ApiCalls.thread.private.markThreadAsRead(threadId);
      // Update the thread in the store
      const { threadStore } = store.getState();
      const { allThreads } = threadStore;
      const updatedThreads = allThreads.map((thread) =>
        thread._id === threadId ? { ...thread, unreadMessages: 0 } : thread
      );
      store.dispatch(ThreadStoreActions.setAllThreads(updatedThreads));
    } catch (error) {
      console.error("Error marking thread as read", error);
    }
  },
  updateThreadMessagePreview: async (threadId, message) => {
    // First call the API to update the message preview
    await ApiCalls.thread.private.updateThread(threadId, {
      lastMessagePreview: message,
    });
    // Then update the store locally
    const { threadStore } = store.getState();
    const { allThreads } = threadStore;
    const updatedThreads = allThreads.map((thread) =>
      thread._id === threadId
        ? { ...thread, lastMessagePreview: message }
        : thread
    );
    store.dispatch(ThreadStoreActions.setAllThreads(updatedThreads));
  },
  deleteThread: async (threadId) => {
    try {
      ThreadHelper.showLoader();
      await ApiCalls.thread.private.deleteThread(threadId);
      //    Also remove the thread from the store
      const { threadStore } = store.getState();
      const { allThreads } = threadStore;
      const updatedThreads = allThreads.filter(
        (thread) => thread._id !== threadId
      );
      store.dispatch(ThreadStoreActions.setAllThreads(updatedThreads));
      ThreadHelper.loadThreadsList();
    } catch (error) {
      console.error("Error deleting thread", error);
    }
    ThreadHelper.hideLoader();
  },
  setActiveThreadsListView: async (viewType) => {
    store.dispatch(ThreadStoreActions.setThreadListActiveView(viewType));
  },
  setSearchText: async (searchText) => {
    store.dispatch(ThreadStoreActions.setSearchText(searchText));
    ThreadHelper.loadThreadsList();
  },
  showLoader: () => {
    store.dispatch(ThreadStoreActions.setLoaderActive(true));
  },
  hideLoader: () => {
    store.dispatch(ThreadStoreActions.setLoaderActive(false));
  },
  getFormattedDateTime: (dateTime, showTimeAlways = false) => {
    // Ensure moment parses the date string correctly
    const inputDate = moment(dateTime, moment.ISO_8601, true);
    const now = moment();
  
    // If the date is invalid, return an error message
    if (!inputDate.isValid()) {
      return "Invalid Date";
    }
  
    // Check if the input date is today
    if (inputDate.isSame(now, "day")) {
      return showTimeAlways ? `Today at ${inputDate.format("hh:mm A")}` : inputDate.format("hh:mm A");
    }
  
    // Check if the input date is yesterday
    if (inputDate.isSame(now.clone().subtract(1, "day"), "day")) {
      return showTimeAlways ? `Yesterday at ${inputDate.format("hh:mm A")}` : "Yesterday";
    }
  
    // Check if the input date is within the last 7 days
    if (now.diff(inputDate, "days") <= 6) {
      return showTimeAlways 
        ? `${inputDate.format("dddd [at] hh:mm A")}` 
        : inputDate.format("dddd");
    }
  
    // Otherwise, return the date in DD MMM, YYYY format, optionally with time if showTimeAlways is true
    return showTimeAlways
      ? inputDate.format("DD MMM, YYYY [at] hh:mm A")
      : inputDate.format("DD MMM, YYYY");
  },
  

  sendMessage: async ({ messageText }) => {
    // Then update the store from the response
    const { threadStore } = store.getState();
    const { selectedThread } = threadStore;
    if (!selectedThread) {
      return;
    }
    const response = await ApiCalls.message.private.sendMessage({
      threadId: selectedThread._id,
      message: messageText,
      messageType: "messages",
    });
    // Trigger a reload of the messages
    ThreadHelper.updateThreadMessagePreview(
      selectedThread._id,
      `You: ${messageText}`
    );
    await MessageHelper.checkNewMessages(selectedThread._id);
  },
};

export default ThreadHelper;
